import { memo, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import AppGlobalsContext from "app/AppGlobalsContext";
import "./ImagesCarousel.scss";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import CarouselDots from "app/pages/.shared/Carousel/CarouselDots/CarouselDots";
import CarouselArrow from "app/pages/SmartDP/Listing/SmartDPProduct/ImagesCarousel/CarouselArrow";
import SwiperLazyBackgroundImage from "app/pages/SmartDP/Listing/SmartDPProduct/ImagesCarousel/SwiperLazyBackgroundImage";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { ICON_SIZES } from "app/constants";

const IMG_SIZES_MOBILE = {
	small: { width: 768, height: 416 },
	medium: { width: 768, height: 416 },
};

const IMG_SIZES_DESKTOP = {
	large: { width: 406, height: 180 },
	xlarge: { width: 406, height: 180 },
};

const CLOUDINARY_OPTIONS = ["c_fill"];

const ImagesCarousel = ({
	images = [],
	alt,
	imgSizesMobile = IMG_SIZES_MOBILE,
	imgSizesDesktop = IMG_SIZES_DESKTOP,
	showImageIndex = false,
	onClick = () => {},
}) => {
	const { resolution } = useContext(AppGlobalsContext);
	const [currentSlide, setCurrentSlide] = useState(0);
	const isMobile = resolution === RESOLUTION.MEDIUM || resolution === RESOLUTION.SMALL;

	const [swiper, setSwiper] = useState(null);

	const slidePrev = () => {
		swiper.slidePrev();
	};

	const slideNext = () => {
		swiper.slideNext();
	};

	useEffect(() => {
		if (swiper) {
			swiper.on("slideChangeTransitionStart", () => {
				setCurrentSlide(swiper.realIndex);
			});
		}

		return () => {
			if (swiper) {
				swiper.off("slideChangeTransitionEnd");
			}
		};
	}, [swiper]);

	return (
		<div className="images-carousel">
			<Swiper
				onSwiper={setSwiper}
				modules={[Pagination]}
				slidesPerView={1}
				pagination={{ el: ".swiper-pagination", type: "bullets", clickable: true }}
				loop={images?.length > 1}
			>
				{images.map((image, index) => {
					return (
						<SwiperSlide key={index}>
							<div onClick={onClick} className="images-carousel__image">
								<SwiperLazyBackgroundImage
									sizes={isMobile ? imgSizesMobile : imgSizesDesktop}
									cloudinaryOptions={CLOUDINARY_OPTIONS}
									alt={alt}
									resolution={resolution}
									src={image.url}
									backgroundPosition="center"
								/>
							</div>
						</SwiperSlide>
					);
				})}
			</Swiper>
			{images?.length > 1 && swiper && (
				<>
					<div className="images-carousel__button--next" onClick={slideNext}>
						<CarouselArrow
							orientation="right"
							className="images-carousel"
							size={ICON_SIZES.MD}
						/>
					</div>
					<div className="images-carousel__button--back" onClick={slidePrev}>
						<CarouselArrow
							orientation="left"
							className="images-carousel"
							size={ICON_SIZES.MD}
						/>
					</div>
					{showImageIndex ? (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.REGULAR}
							className="images-carousel__image-index"
							component="span"
							isBold
						>
							{`${currentSlide + 1}/${images?.length}`}
						</Typography>
					) : (
						<CarouselDots
							currentSlide={currentSlide}
							totalSlides={images?.length}
							size={isMobile ? 7 : 8}
						/>
					)}
				</>
			)}
		</div>
	);
};

ImagesCarousel.propTypes = {
	images: PropTypes.array,
	imgSizesMobile: PropTypes.object,
	imgSizesDesktop: PropTypes.object,
	alt: PropTypes.string,
	showImageIndex: PropTypes.bool,
	onClick: PropTypes.func,
};

export default memo(ImagesCarousel);
